import { Injectable } from "@angular/core";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DbCrudHelper, PayloadWithLogging, RowsAffected } from "@app/shared/services/db-crud-helper";
import { TSLogActions, TSLogObjectTypes, TSLoggingService } from "@app/shared/services/ts-logging.service";
import { Observable, combineLatest, map, take } from "rxjs";
import { EmergencyContact, EmergencyContactData, EmergencyContactHelper } from "../model/emergency-contact";

export type CreateEmergencyContactPayload = Omit<EmergencyContact, "id">;

@Injectable({
    providedIn: "root",
})
export class EmergencyContactDbService {
    helper = new EmergencyContactHelper();
    // crudHelper = new DbCrudHelper(this.websocketService);
    crudHelperService: ClareWSCrudHelperService<EmergencyContact, EmergencyContactData>;

    constructor(protected tsLoggingService: TSLoggingService, protected crudHelper: DbCrudHelper) {
        this.crudHelperService = new ClareWSCrudHelperService(tsLoggingService, crudHelper, this.helper);
    }

    getEmergencyContacts(projectId: number): Observable<ClareDbCollection<EmergencyContact> | null> {
        return this.crudHelperService
            .retrieve({
                data: { id: projectId },
                endpoint: "GetProjectEmergencyContact",
            })
            .pipe(
                map(contacts => contacts.map(c => this.helper.fromDbObject(c))),
                map(contacts =>
                    contacts.reduce((collection, c) => {
                        collection[c.id] = c;
                        return collection;
                    }, {})
                )
            );
    }

    createEmergencyContact(contact: EmergencyContact): Observable<EmergencyContact> {
        return this.crudHelperService
            .create({
                endpoint: "CreateEmergencyContact",
                data: contact,
                logData: { contact },
            })
            .pipe(map(data => this.helper.fromDbObject(data)));
    }

    bulkUpdate(emergencyContacts: EmergencyContact[]): Observable<RowsAffected[]> {
        // This isn't atomic...
        const ops = emergencyContacts.map(data => this.updateContact(data));
        return combineLatest(ops).pipe(take(1));
    }

    updateContact(contact: EmergencyContact) {
        return this.crudHelperService.update({
            endpoint: "UpdateEmergencyContact",
            data: contact,
            logData: { contact },
        });
    }

    deleteContact(contact: EmergencyContact): Observable<RowsAffected> {
        return this.crudHelperService.delete({
            endpoint: "DeleteEmergencyContact",
            id: contact.id,
            logData: { contact },
        });
    }

    bulkDelete({ data, logData }: PayloadWithLogging<EmergencyContact[]>): Observable<RowsAffected[]> {
        this.tsLoggingService.logEvent(
            null,
            EmergencyContactHelper.dbTypeName,
            TSLogObjectTypes.ssopgrest,
            TSLogActions.delete,
            null,
            logData
        );

        // This isn't atomic...
        const ops = data.map(contact => this.deleteContact(contact));
        return combineLatest(ops).pipe(take(1));
    }
}
